import { ComponentProps, FC, useEffect } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box } from '@altermaind/web/ui';
import { Claims } from '@/_shared';
import {
  PaginatedTable,
  usePaginatedTable,
  Column,
  StyledTable,
  ValueInTable,
  ClaimsComponent,
  Tooltip,
} from '@/_shared/components';
import { getUserProfileCall } from '@/core/state/api';
import { getDelegatesCall } from '@/organization/state';
import { User } from '@/organization/state/types';
import { SortOrder } from '@/swagger/constants/slash-enums';

type Props = {
  setSelectedDelegate: (delegate: User | undefined) => void;
  selectedDelegate: User | undefined;
  paginedProps: ReturnType<typeof usePaginatedTable>;
  drawer: ComponentProps<typeof StyledTable>['drawer'];
  openDialogRemoveDelegate: () => void;
  openDialogResendInviteDelegate: () => void;
};

export const TableDelegates: FC<Props> = ({
  setSelectedDelegate,
  selectedDelegate,
  paginedProps,
  drawer,
  openDialogRemoveDelegate,
  openDialogResendInviteDelegate,
}) => {
  const t = useTranslations('settings.tabs.delegates.table');
  const tShared = useTranslations();

  const {
    loading: loadingGetDelegates,
    success: successGetDelegates,
    error: errorGetDelegates,
    execute: executeGetDelegates,
    reset: resetGetDelegates,
  } = getDelegatesCall.hook();

  const { email: currentUserEmail } = getUserProfileCall.hook();

  const columns: Column[] = [
    {
      key: 'firstName',
      sortKey: 'firstName',
      sortable: true,
      title: t('firstName'),
      renderValue: (item: User) => (
        <ValueInTable>{`${item.firstName} ${item.lastName}`}</ValueInTable>
      ),
      width: 250,
    },
    {
      key: 'email',
      sortable: true,
      title: t('email'),
      renderValue: (item: User) => <ValueInTable>{item.email}</ValueInTable>,
    },
    {
      key: 'status',
      title: t('status.label'),
      renderValue: (item: User) => (
        <ValueInTable>{item.status ? tShared(`userStatus.${item.status}`) : ''}</ValueInTable>
      ),
      width: 150,
    },
    {
      key: 'edit',
      title: '',
      width: '90px',
      renderValue: (item: User) =>
        item.email === selectedDelegate?.email && item.email !== currentUserEmail ? (
          <ClaimsComponent claimKey={Claims.ORGANIZATION_WRITE}>
            <Box
              display='flex'
              flexDirection='row'
              alignItems='flex-end'
              justifyContent='flex-end'
              width='100%'
            >
              {item.status === 'Pending' ? (
                <Box style={{ cursor: 'pointer' }} onClick={openDialogResendInviteDelegate}>
                  <Tooltip iconProps={{ size: 's' }} iconName='refresh'>
                    {t('tooltips.resendInvite')}
                  </Tooltip>
                </Box>
              ) : (
                <Box minWidth='25px' />
              )}
              <Box ml='xxs' style={{ cursor: 'pointer' }} onClick={openDialogRemoveDelegate}>
                <Tooltip iconProps={{ size: 's' }} iconName='trash'>
                  {t('tooltips.remove')}
                </Tooltip>
              </Box>
            </Box>
          </ClaimsComponent>
        ) : null,
    },
  ];

  useEffect(() => {
    if (!loadingGetDelegates && !successGetDelegates && !errorGetDelegates) {
      executeGetDelegates({
        start: 0,
        length: 1000,
      });
    }

    return () => {
      resetGetDelegates();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (loadingGetDelegates) {
      setSelectedDelegate(undefined);
    }
  }, [loadingGetDelegates, setSelectedDelegate]);

  return (
    <StyledTable
      hasElement={selectedDelegate !== undefined}
      error={!!errorGetDelegates}
      loading={loadingGetDelegates}
      drawer={drawer}
    >
      <PaginatedTable
        header-data-testid='table-delegates-header'
        row-data-testid='table-delegates-row'
        columns={columns}
        pageSize={1000}
        call={{ ...getDelegatesCall }}
        onRowClick={(delegate: User) => {
          setSelectedDelegate(delegate);
        }}
        onHoverRow={(delegate: User) => {
          setSelectedDelegate(delegate);
        }}
        rowWrapperProps={(delegate: User) => {
          return delegate.email === selectedDelegate?.email
            ? {
                backgroundColor: 'grey/dark/background',
                borderRadius: '10px',
                position: 'relative',
              }
            : {};
        }}
        paginedProps={paginedProps}
        loading={loadingGetDelegates}
        skeletonProps={{
          hasHeaderRow1: false,
          hasHeaderRow2: true,
          rows: 1,
        }}
        hasHover
        defaultOrderBy={{ firstName: SortOrder.ASC }}
      />
    </StyledTable>
  );
};
